import { FC, useEffect, useState } from 'react';
import { Field } from 'react-final-form';
import { BsCalendar4, BsDiagram2, BsHouseFill } from 'react-icons/bs';
import { Form as BootstrapForm } from 'react-bootstrap';
import { isEmpty } from 'lodash';

import { Box, Button, getReactSelectStyles, RFFCheckInput, RFFReactSelect } from '@qwealth/qcore';
import { loadTeam, QTask, TASK_PRIORITIES, TASK_STATUS_LIST, REGULAR, TASK_FREQUENCY, TASK_COMPLETED } from '@qwealth/qdata';
import { commonSubscription, RFFMaskedDateField } from '@qwealth/kyc';

import { useAppDispatch, useAppSelector } from 'data/store';
import HouseholdSearch from 'components/common/HouseholdSearch';
import { ExtendedSearchOption } from 'data/models/ExtendedSearchOption';
import NavigationLink from 'components/common/NavigationLink';
import { ModalModeOptions, MODAL_MODE_VIEW, MODAL_MODE_ADD, MODAL_MODE_EDIT } from 'data/models/Activity';
import { StyledLabel } from './AddTask.styled';
import ContactSelect from '../ContactSelect';
import { QTaskForm } from 'data/models/taskForm';
import { selectHouseholdTasksByRecurringTaskId, selectQBoardTasksByRecurringTaskId } from 'data/selectors/activitiesSelectors';
import { formatDate } from 'data/helpers/dateTimeHelper';

interface LinkedHouseholdContent {
  label: string;
  link: string;
}

interface Props {
  modalMode: ModalModeOptions;
  task: Partial<QTask> | null;
  formValues: QTaskForm;
  isQFeed: boolean;
  isTaskModalFromWorkflow?: boolean;
  setShowWorkflowModal?: React.Dispatch<React.SetStateAction<boolean>>;
  close?: () => void;
}

const TaskSidebar: FC<Props> = ({ modalMode, task, formValues, isQFeed, isTaskModalFromWorkflow, setShowWorkflowModal, close }) => {
  const dispatch = useAppDispatch();
  const [selectedHousehold, setSelectedHousehold] = useState<ExtendedSearchOption>();
  const [linkHouseholdContent, setLinkHouseholdContent] = useState<LinkedHouseholdContent>({
    label: '',
    link: '',
  });
  const selectTasksByRecurringTaskId: QTask[] = isQFeed
    ? useAppSelector(state => selectQBoardTasksByRecurringTaskId(state, formValues?.recurringTaskId))
    : useAppSelector(state => selectHouseholdTasksByRecurringTaskId(state, formValues?.recurringTaskId));

  const hasWorkflow = task?.workflowName;
  const isWorkflowModalDisabled = modalMode === MODAL_MODE_EDIT || isTaskModalFromWorkflow || task?.workflowDeletedFlag || !setShowWorkflowModal;

  useEffect(() => {
    if (modalMode === MODAL_MODE_VIEW && task) {
      if (task?.householdQID && task?.householdName) {
        const linkHouseholdContent: LinkedHouseholdContent = {
          label: task?.householdName,
          link: task?.householdType === REGULAR ? `/household?household=${task?.householdQID}` : `/prospect-households/${task?.householdQID}`,
        };
        setLinkHouseholdContent(linkHouseholdContent);
      }
    }
  }, [task]);

  const upcomingDueDates = (): string[] => {
    if (isEmpty(selectTasksByRecurringTaskId)) {
      return [];
    } else {
      const findTaskIndex = selectTasksByRecurringTaskId.findIndex(task => task.id === formValues?.id);
      if (findTaskIndex === -1) {
        return [];
      }
      const upcomingTasks = selectTasksByRecurringTaskId.filter((task, index) => index > findTaskIndex).slice(0, 2);
      // handle null dueDate for upcoming tasks in recurring tasks. ToDo: Make a better solution as part of workflows
      return isEmpty(upcomingTasks) ? [] : upcomingTasks.map(task => (task.dueDate ? formatDate(task.dueDate, 'MMM d, yyyy') : 'N/A'));
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="default" flexBasis="250px" flexShrink={1} flexGrow={1}>
      {hasWorkflow && (
        <Box display="flex" flexDirection="column" gap="small">
          <Box fontWeight="bold">Workflow</Box>
          <Button
            variant="text"
            color="gray.1"
            disabled={isWorkflowModalDisabled}
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="small"
            size="small"
            onClick={() => {
              setShowWorkflowModal?.(true);
              close?.();
            }}
            flexWrap="wrap"
          >
            <BsDiagram2 size="16px" />
            {task.workflowName}
          </Button>
        </Box>
      )}
      {modalMode !== MODAL_MODE_ADD && (
        <Box display="flex" flexDirection="column" gap="small">
          <Box fontWeight="bold">Household</Box>
          <NavigationLink content={linkHouseholdContent} icon={<BsHouseFill size="14px" />} isDisabled={!isQFeed} />
        </Box>
      )}
      {isQFeed && modalMode === MODAL_MODE_ADD && (
        <Box display="flex" flexDirection="column">
          <StyledLabel>Household*</StyledLabel>
          <Field name="householdQID" subscription={commonSubscription}>
            {({ input, meta }) => (
              <Box>
                <HouseholdSearch
                  value={selectedHousehold}
                  onChange={opt => {
                    input.onChange(opt.value);
                    setSelectedHousehold(opt);
                    const householdCode = opt.household.iaCode ?? opt.household.insuranceCode;
                    if (householdCode) {
                      dispatch(loadTeam(householdCode));
                    }
                  }}
                  customStyles={getReactSelectStyles(!!meta.touched && !!meta.error)}
                />
                {meta.error && meta.touched && <Box color="error">{meta.error}</Box>}
              </Box>
            )}
          </Field>
        </Box>
      )}
      <Box display="flex" flexDirection="column">
        <StyledLabel>Assigned*</StyledLabel>
        <Field name="assignee" subscription={commonSubscription}>
          {({ input, meta }) => (
            <Box>
              <ContactSelect
                {...input}
                disabled={formValues?.status === TASK_COMPLETED || modalMode === MODAL_MODE_VIEW}
                onBlur={input.onBlur}
                onChange={selected => {
                  input.onChange(selected);
                }}
                value={formValues.assignee || null}
                customStyles={getReactSelectStyles(!!meta.touched && !!meta.error)}
              />
              {meta.error && meta.touched && <Box color="error">{meta.error}</Box>}
            </Box>
          )}
        </Field>
      </Box>
      <Box display="flex" flexDirection="column">
        <RFFReactSelect
          label="Status*"
          name="status"
          subscription={commonSubscription}
          inputFieldProps={{
            options: [...TASK_STATUS_LIST],
            isDisabled: modalMode === MODAL_MODE_VIEW,
          }}
        />
      </Box>
      <Box display="flex" flexDirection="column">
        <RFFReactSelect
          label="Priority*"
          name="priority"
          subscription={commonSubscription}
          inputFieldProps={{
            options: [...TASK_PRIORITIES],
            isDisabled: formValues?.status === TASK_COMPLETED || modalMode === MODAL_MODE_VIEW,
          }}
        />
      </Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <StyledLabel>Share with Client</StyledLabel>
        <Field name="isInternal">
          {({ input }) => (
            <BootstrapForm.Check
              {...input}
              type="checkbox"
              id="custom-switch"
              label=""
              disabled={formValues?.status === TASK_COMPLETED || modalMode !== MODAL_MODE_ADD}
              checked={input.value !== 1}
              onChange={e => {
                input.onChange(e.target.value === '1' ? 0 : 1);
              }}
            />
          )}
        </Field>
      </Box>
      <Box display="flex" flexDirection="column">
        <RFFMaskedDateField
          name="dueDate"
          fieldSubscription={commonSubscription}
          label="Due Date*"
          inputFieldProps={{
            minDate: new Date(),
            disabled: formValues?.status === TASK_COMPLETED || modalMode === MODAL_MODE_VIEW,
          }}
        />
      </Box>
      {modalMode === MODAL_MODE_ADD && (
        <>
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <RFFCheckInput
              label="Make Recurring"
              name="isRecurring"
              subscription={commonSubscription}
              inputFieldProps={{
                disabled: modalMode !== MODAL_MODE_ADD,
              }}
            />
          </Box>

          {formValues.isRecurring && (
            <>
              <Box display="flex" flexDirection="column">
                <RFFReactSelect
                  label="Frequency*"
                  name="frequency"
                  subscription={commonSubscription}
                  inputFieldProps={{
                    options: [...TASK_FREQUENCY],
                  }}
                />
              </Box>

              <Box display="flex" flexDirection="column">
                <RFFMaskedDateField
                  name="endDate"
                  fieldSubscription={commonSubscription}
                  label="End Date*"
                  inputFieldProps={{
                    minDate: new Date(),
                  }}
                />
              </Box>
            </>
          )}
        </>
      )}
      {modalMode !== MODAL_MODE_ADD && formValues.isRecurring && (
        <>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap="default">
            <Box display="flex" flexDirection="column" gap="default">
              <Box as="span" fontWeight="bold">
                Starting
              </Box>
              <Box as="span">{formValues.startDate}</Box>
            </Box>

            <Box display="flex" flexDirection="column" gap="default" justifySelf="flex-end">
              <Box as="span" fontWeight="bold">
                Ending
              </Box>
              <Box as="span">{formValues.endDate}</Box>
            </Box>

            <Box display="flex" flexDirection="column" gap="default">
              <Box as="span" fontWeight="bold">
                Frequency
              </Box>
              <Box as="span">{formValues.frequency}</Box>
            </Box>
          </Box>

          {isEmpty(upcomingDueDates()) ? (
            <Box as="span" fontStyle="italic">
              No Upcoming Due Dates
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap="large">
              <Box as="span" fontWeight="bold">
                Upcoming Due Dates
              </Box>
              {upcomingDueDates().map((value, index) => (
                <Box display="flex" flexDirection="row" gap="default" key={'upcomingDueDate' + index}>
                  <BsCalendar4 size="18px" strokeWidth="0.5" />
                  <Box as="span">{value}</Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TaskSidebar;
